import { useState } from "react";
import { Briefcase, FilePdf, Spinner } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { PortfolioSummaryCards } from "./PortfolioSummaryCards";
import { PortfolioPnlChart } from "./PortfolioPnlChart";
import { PortfolioItemRow } from "./PortfolioItemRow";
import { getCardClass, getTextPrimary, getTextSecondary, getDividerClass, getInactiveFilterClass } from "../../lib/styleHelpers";

const FILTER_LABELS = { all: "All", open: "Open", closed: "Closed" };

export function PortfolioTab({
  portfolio,
  summary,
  onUpdateItem,
  onRemoveItem,
  onViewOpportunity,
  onExportPdf,
  exportingPdf,
  isDark,
}) {
  const [filter, setFilter] = useState("all");

  const filtered =
    filter === "all" ? portfolio : portfolio.filter((i) => i.status === filter);

  const pnlChartData = filtered.map((item) => ({
    name:
      item.opportunity_title.length > 20
        ? item.opportunity_title.substring(0, 20) + "..."
        : item.opportunity_title,
    pnl: item.pnl,
    fill: item.pnl >= 0 ? "#10b981" : "#ef4444",
  }));

  return (
    <div className="animate-fade-in space-y-6" data-testid="portfolio-content">
      <PortfolioSummaryCards summary={summary} isDark={isDark} />
      <PortfolioPnlChart data={pnlChartData} isDark={isDark} />

      {/* Filter + List */}
      <div className={`rounded-xl border ${getCardClass(isDark)}`}>
        <div className={`p-4 border-b flex items-center justify-between ${isDark ? "border-white/10" : "border-gray-200"}`}>
          <div>
            <h2 className={`text-lg font-bold ${getTextPrimary(isDark)}`}>
              <Briefcase size={20} weight="fill" className="inline text-purple-500 mr-2" />
              Investments
            </h2>
            <p className={`text-sm font-body ${getTextSecondary(isDark)}`}>
              {filtered.length} position{filtered.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {portfolio.length > 0 && (
              <Button
                onClick={onExportPdf}
                disabled={exportingPdf}
                className="text-xs rounded-lg mr-2 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="export-pdf-btn"
              >
                {exportingPdf ? (
                  <><Spinner size={14} className="animate-spin mr-1" />Exporting...</>
                ) : (
                  <><FilePdf size={14} className="mr-1" />Export PDF</>
                )}
              </Button>
            )}
            {["all", "open", "closed"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  filter === f
                    ? "bg-purple-600 text-white"
                    : getInactiveFilterClass(isDark)
                }`}
                data-testid={`portfolio-filter-${f}`}
              >
                {FILTER_LABELS[f]}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Briefcase size={48} className={`mx-auto mb-4 ${isDark ? "text-gray-600" : "text-gray-300"}`} />
            <h3 className={`text-lg font-bold mb-2 ${getTextPrimary(isDark)}`}>
              {portfolio.length === 0 ? "No investments yet" : `No ${filter} positions`}
            </h3>
            <p className={`font-body text-sm ${getTextSecondary(isDark)}`}>
              {portfolio.length === 0
                ? 'Mark watchlist items as "Executed" and click "Track in Portfolio" to start tracking'
                : "Try a different filter"}
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[500px]">
            <div className={getDividerClass(isDark)} style={{ display: "flex", flexDirection: "column" }}>
              {filtered.map((item) => (
                <PortfolioItemRow
                  key={item.id}
                  item={item}
                  onUpdateItem={onUpdateItem}
                  onRemoveItem={onRemoveItem}
                  onViewOpportunity={onViewOpportunity}
                  isDark={isDark}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
